import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../styles/resume.css";
import resumePhoto1 from "../photos/Resume01.png";
import resumePhoto2 from "../photos/Resume02.jpeg";
import resumePhoto3 from "../photos/Resume03.jpg";
import resumePDF from "../photos/RESUME.pdf";
import resumePhoto4 from "../photos/Resume04.jpg";

// ── Scroll-reveal: watches the .content scroll container ──
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const scrollRoot = document.getElementById("resume-scroll-root");
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      {
        root: scrollRoot || null,
        threshold: 0.08,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "", style = {} }) { // ✅ ADDED style prop
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`rv ${visible ? "rv--in" : ""} ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        ...style // ✅ ADDED: forward style (this fixes your background image)
      }}
    >
      {children}
    </div>
  );
}

// ── Side tabs rendered into document.body via portal ──
function SideTabs() {
  const [previewHovered, setPreviewHovered] = useState(false);
  const [downloadHovered, setDownloadHovered] = useState(false);

  return createPortal(
    <div className="rp-side">
      {/* Preview tab */}
      <div
        className={`rp-tab${previewHovered ? " rp-tab--hov" : ""}`}
        onMouseEnter={() => setPreviewHovered(true)}
        onMouseLeave={() => setPreviewHovered(false)}
      >
        <span className="rp-tab__label">Resume Preview</span>
        {previewHovered && (
          <div className="rp-tab__panel">
            <iframe
              src={resumePDF}
              className="rp-tab__frame"
              title="Resume Preview"
            />
          </div>
        )}
      </div>

      {/* Download tab */}
      <div
        className={`rp-tab${downloadHovered ? " rp-tab--hov" : ""}`}
        onMouseEnter={() => setDownloadHovered(true)}
        onMouseLeave={() => setDownloadHovered(false)}
      >
        <span className="rp-tab__label">Download Resume</span>
        {downloadHovered && (
          <div className="rp-tab__panel rp-tab__panel--sm">
            <p className="rp-tab__dl-hint">Click to download</p>
            <a
              href={resumePDF}
              download="Justin_Zedrick_Tundag_Resume.pdf"
              className="rp-tab__dl-btn"
            >
              ⬇ Download PDF
            </a>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Data ──
const experiences = [
  {
    category: "Hardware Description Language",
    period: "2025 – Present",
    collaborators: null,
    items: [
      "Smart Streetlight Project using HDL verification and testbench",
      "Mealy Vending Machine Simulation using FPGA Board with Quartus II IDE",
    ],
  },
  {
    category: "Object Oriented Programming",
    period: "2024 – 2025",
    collaborators: "with Carla Jem Banzuela",
    items: [
      "Piktyur Piktyur! — a photobooth application built with OOP principles",
      "Sign Detection and Warning System for Visually Impaired Individuals using YOLO",
    ],
  },
  {
    category: "Digital Signal Processing",
    period: "2025 – 2026",
    collaborators: "with Roxanne Velasco and Hannah Laine Kuizon",
    items: [
      "Sift Happens — a digital filtering device using a DIY anemometer that reads and filters analog-to-digital data",
    ],
  },
  {
    category: "Feedback and Control Systems",
    period: "2026 – Present",
    collaborators: "with Kurt Aldrin Egenias and Rayne Lance Buendia",
    items: [
      "Bomb Disposal Robot Prototype — a closed-loop FCS robotics prototype",
    ],
  },
];

const whatIDo = [
  {
    title: "Coding",
    body: "I write clean, purposeful code across multiple languages — from low-level C and HDL to web-facing HTML, CSS, and JavaScript.",
  },
  {
    title: "Design",
    body: "I design interfaces with intention — prioritizing clarity, usability, and a strong visual point of view.",
  },
  {
    title: "Engineering",
    body: "I approach hardware and software with the same analytical mindset — building systems that are efficient, reliable, and scalable.",
  },
];

const languages = ["HDL (Verilog)", "C", "Python", "HTML", "CSS", "JavaScript", "Java"];

export default function Resume() {
  return (
    <div className="rp">
      <SideTabs />

      {/* ════ HERO ════ */}
      <Reveal
        className="rp-hero"
        style={{
          "--bg-image": `url(${resumePhoto4})`, // keep this
        }}
      >
        <div className="rp-hero__left">
          <img src={resumePhoto1} alt="Justin Zedrick" className="rp-hero__img" />
        </div>
        <div className="rp-hero__right">
          <h1 className="rp-hero__name">Justin Zedrick<br />P. Tundag</h1>
          <p className="rp-hero__role">Computer Engineering Student</p>
          <div className="rp-hero__tags">
            <span>Hardware Description Language</span>
            <span>Web &amp; UI Design</span>
            <span>Front &amp; Back End Coding</span>
          </div>
          <p className="rp-hero__tagline">
            A builder at heart — driven by curiosity, precision, and the belief
            that good engineering is indistinguishable from good design.
          </p>
        </div>
      </Reveal>

      {/* ════ PHILOSOPHY ════ */}
      <Reveal delay={80} className="rp-philosophy">
        <h2 className="rp-section-title">My Engineering<br />Philosophy</h2>
        <p className="rp-philosophy__body">
          Engineering is not just about making things work — it is about making
          things work <em>well</em>. Every circuit, every line of code, and
          every system I build is a deliberate act. I pay careful attention to
          efficiency, clarity, and real-world impact. No project is too small
          to build with intention, and no problem is too complex to break down
          with the right system of thinking.
        </p>
      </Reveal>

      {/* ════ EXPERIENCE ════ */}
      <div className="rp-experience">
        <Reveal>
          <h2 className="rp-section-title">My Work</h2>
        </Reveal>
        <div className="rp-exp-grid">
          {experiences.map((exp, i) => (
            <Reveal key={exp.category} delay={i * 70}>
              <div className="rp-exp-card">
                <p className="rp-exp-card__cat">{exp.category}</p>
                <p className="rp-exp-card__period">{exp.period}</p>
                {exp.collaborators && (
                  <p className="rp-exp-card__collab">{exp.collaborators}</p>
                )}
                <ul className="rp-exp-card__list">
                  {exp.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ════ QUOTE BANNER ════ */}
      <Reveal className="rp-banner">
        <div className="rp-banner__bg">
          <img src={resumePhoto3} alt="background" className="rp-banner__bg-img" />
        </div>
        <p className="rp-banner__quote">
          Every problem is a <em>system</em><br />waiting to be understood.
        </p>
      </Reveal>

      {/* ════ WHAT I DO ════ */}
      <div className="rp-whatido">
        <Reveal>
          <h2 className="rp-section-title">What I Do</h2>
        </Reveal>
        <div className="rp-whatido__grid">
          {whatIDo.map((w, i) => (
            <Reveal key={w.title} delay={i * 70}>
              <div className="rp-whatido__card">
                <p className="rp-whatido__title">{w.title}</p>
                <p className="rp-whatido__body">{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ════ EDUCATION & CONTACT ════ */}
      <Reveal className="rp-bottom">
        <div className="rp-bottom__left">
          <h2 className="rp-section-title">Education &amp; Contact</h2>
          <div className="rp-contact-list">
            <div className="rp-contact-item">
              <span className="rp-contact-item__label">LinkedIn</span>
              <span className="rp-contact-item__val">Justin Tundag</span>
            </div>
            <div className="rp-contact-item">
              <span className="rp-contact-item__label">School</span>
              <span className="rp-contact-item__val">First Asia Institute of Technology and Humanities — BS Computer Engineering, Major in Microelectronics (2023–2027)</span>
            </div>
            <div className="rp-contact-item">
              <span className="rp-contact-item__label">Email</span>
              <span className="rp-contact-item__val">justundag@gmail.com</span>
            </div>
            <div className="rp-contact-item">
              <span className="rp-contact-item__label">Phone</span>
              <span className="rp-contact-item__val">+6390-6434-9414</span>
            </div>
            <div className="rp-contact-item">
              <span className="rp-contact-item__label">Address</span>
              <span className="rp-contact-item__val">Santiago, Malvar, Batangas</span>
            </div>
          </div>
          <div className="rp-langs">
            <p className="rp-langs__label">— Programming Languages —</p>
            <div className="rp-langs__list">
              {languages.map((l) => (
                <span key={l} className="rp-langs__tag">{l}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="rp-bottom__right">
          <img src={resumePhoto2} alt="Justin Zedrick" className="rp-hero__img" />
        </div>
      </Reveal>

    </div>
  );
}